import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { User, Save, ArrowLeft, Heart } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '@/contexts/LanguageContext';
import Navbar from '@/components/Navbar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// International dialing codes — minimal set (top IT/EU/global).
const PREFIXES = [
  '+39', '+33', '+34', '+44', '+49', '+41', '+43', '+32', '+31',
  '+351', '+1', '+1 (CA)', '+61', '+55', '+54', '+52', '+86', '+81',
  '+91', '+971',
].map(p => ({ value: p.split(' ')[0], label: p }));

// "05031990" -> "05/03/1990"
const maskDate = (v) => {
  const d = v.replace(/\D/g, '').slice(0, 8);
  return [d.slice(0, 2), d.slice(2, 4), d.slice(4, 8)].filter(Boolean).join('/');
};

// "05/03/1990" -> "1990-03-05", null if not a real date
const parseDate = (v) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(v);
  if (!m) return null;
  const [, dd, mm, yyyy] = m;
  const dt = new Date(Date.UTC(+yyyy, +mm - 1, +dd));
  if (+yyyy < 1900 || dt.getUTCDate() !== +dd || dt.getUTCMonth() !== +mm - 1) return null;
  return `${yyyy}-${mm}-${dd}`;
};

const minBirthDate = (years) => {
  const n = new Date();
  return `${n.getFullYear() - years}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};

export default function ProfileEdit() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const { t, lang } = useLang();

  const [form, setForm] = useState({
    name: '',
    surname: '',
    mobile_prefix: '+39',
    mobile_number: '',
    birth_date: '',
    city: '',
    gender: '',
    social_interest_category_ids: [],
  });
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const cr = await axios.get(`${API}/social-categories`);
        setCategories(cr.data || []);
      } catch (e) { /* non-blocking */ }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (!user) return;
    setForm({
      name: user.name || '',
      surname: user.surname || '',
      mobile_prefix: user.mobile_prefix || '+39',
      mobile_number: user.mobile_number || '',
      birth_date: user.birth_date ? user.birth_date.split('-').reverse().join('/') : '',
      city: user.city || '',
      gender: user.gender || '',
      social_interest_category_ids: user.social_interest_category_ids || [],
    });
  }, [user]);

  const toggleCategory = (id) => {
    setForm(f => ({
      ...f,
      social_interest_category_ids: f.social_interest_category_ids.includes(id)
        ? f.social_interest_category_ids.filter(x => x !== id)
        : [...f.social_interest_category_ids, id],
    }));
  };

  const onSave = async () => {
    const bd = form.birth_date ? parseDate(form.birth_date) : '';
    if (bd === null) { toast.error(t('pe_birth_date_invalid')); return; }
    if (bd && bd > new Date().toISOString().slice(0, 10)) { toast.error(t('pe_birth_date_future')); return; }
    if (bd && bd > minBirthDate(14)) { toast.error(t('pe_birth_date_min_age')); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        surname: form.surname.trim(),
        mobile_prefix: form.mobile_prefix,
        mobile_number: form.mobile_number.trim(),
        birth_date: bd,
        city: form.city.trim(),
        gender: form.gender,
        social_interest_category_ids: form.social_interest_category_ids,
      };
      await axios.patch(`${API}/users/me/profile`, payload, { withCredentials: true });
      toast.success(t('pe_saved'));
      if (checkAuth) await checkAuth();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('pe_error'));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !user) return (
    <div className="min-h-screen bg-background pb-20 md:pb-8">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 py-8 pt-20 sm:pt-24 text-muted-foreground font-sans">…</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-8" data-testid="profile-edit-page">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 pt-20 sm:pt-24">
        <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="mb-4" data-testid="pe-back">
          <ArrowLeft className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> {t('nav_dashboard')}
        </Button>
        <div className="flex items-center gap-3 mb-6">
          <User className="w-6 h-6 text-primary" strokeWidth={1.5} />
          <h1 className="text-3xl font-serif font-bold text-primary">{t('pe_title')}</h1>
        </div>

      <Card className="rounded-2xl border border-border mb-5">
        <CardContent className="p-6 space-y-4">
          <h2 className="text-lg font-serif font-semibold text-foreground mb-2">{t('pe_section_personal')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="pe-name">{t('pe_name')}</Label>
              <Input id="pe-name" data-testid="pe-name" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} />
            </div>
            <div>
              <Label htmlFor="pe-surname">{t('pe_surname')}</Label>
              <Input id="pe-surname" data-testid="pe-surname" value={form.surname} onChange={e => setForm(f => ({...f, surname: e.target.value}))} />
            </div>
            <div>
              <Label>{t('pe_mobile_number')}</Label>
              <div className="flex gap-2">
                <Select value={form.mobile_prefix} onValueChange={v => setForm(f => ({...f, mobile_prefix: v}))}>
                  <SelectTrigger className="w-28" data-testid="pe-mobile-prefix"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PREFIXES.map(p => <SelectItem key={p.label} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input placeholder="333 1234567" data-testid="pe-mobile-number" className="flex-1" value={form.mobile_number} onChange={e => setForm(f => ({...f, mobile_number: e.target.value}))} />
              </div>
            </div>
            <div>
              <Label htmlFor="pe-birth-date">{t('pe_birth_date')}</Label>
              <Input id="pe-birth-date" inputMode="numeric" placeholder={t('pe_birth_date_ph')} maxLength={10} data-testid="pe-birth-date" value={form.birth_date} onChange={e => setForm(f => ({...f, birth_date: maskDate(e.target.value)}))} />
            </div>
            <div>
              <Label htmlFor="pe-city">{t('pe_city')}</Label>
              <Input id="pe-city" data-testid="pe-city" value={form.city} onChange={e => setForm(f => ({...f, city: e.target.value}))} />
            </div>
            <div>
              <Label>{t('pe_gender')}</Label>
              <Select value={form.gender} onValueChange={v => setForm(f => ({...f, gender: v}))}>
                <SelectTrigger data-testid="pe-gender"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="woman">{t('pe_gender_woman')}</SelectItem>
                  <SelectItem value="man">{t('pe_gender_man')}</SelectItem>
                  <SelectItem value="non_binary">{t('pe_gender_non_binary')}</SelectItem>
                  <SelectItem value="prefer_not_to_say">{t('pe_gender_prefer_not_to_say')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border border-border mb-5">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-2">
            <Heart className="w-5 h-5 text-primary" strokeWidth={1.5} />
            <h2 className="text-lg font-serif font-semibold text-foreground">{t('pe_interests_title')}</h2>
          </div>
          <p className="text-sm text-muted-foreground font-sans mb-4">{t('pe_interests_intro')}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="pe-interests-grid">
            {categories.map(c => (
              <label key={c.category_id} className="flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg border border-border hover:border-primary/40 transition-colors" data-testid={`pe-cat-${c.category_id}`}>
                <Checkbox
                  checked={form.social_interest_category_ids.includes(c.category_id)}
                  onCheckedChange={() => toggleCategory(c.category_id)}
                />
                <span className="text-sm font-sans">{lang === 'en' ? c.label_en : c.label_it}</span>
              </label>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={onSave} disabled={saving} data-testid="pe-save" className="btn-lift">
          <Save className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t('pe_save')}
        </Button>
      </div>
      </div>
    </div>
  );
}
